import React, { useState, useMemo } from 'react';
import { Table, Card, Badge, Image, Spinner, Form, Row, Col, Button, InputGroup } from 'react-bootstrap';
import { Package, Copy, ChevronRight, ExternalLink, X } from 'lucide-react';
import type { SkinSeoulAdminProduct } from '../../../../models/ProductTypes';
import { PublicationStatus, StockStatus, SkinSeoulProductType } from '../../../../models/ProductTypes';
import type { GpKeywordCpSearch } from '../../../../models/Coupang';
import { getConfig } from '../../../../config';
import { getPublicationStatusColor, getStockStatusColor, getProductTypeColor } from '../../../../utils/statusUtils';
import { ProductDetailsModal } from './ProductDetailsModal';

interface ClientFilters {
  hasGpId: 'all' | 'yes' | 'no';
  wave: 'all' | 'manual' | '1' | '2' | '3' | '4' | '5' | '6';
  brand: string;
  category: string;
  productName: string;
  imageCount: 'all' | 'none' | 'some';
  cpInfo: 'all' | 'yes' | 'no';
}

interface SkinSeoulProductsTableProps {
  products: SkinSeoulAdminProduct[];
  selectedProduct: SkinSeoulAdminProduct | null;
  selectedVariation: SkinSeoulAdminProduct | null;
  onProductSelect: (product: SkinSeoulAdminProduct) => void;
  onVariationSelect: (variation: SkinSeoulAdminProduct) => void;
  loading: boolean;
  totalProducts: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  postStatus: PublicationStatus | 'All';
  onPostStatusChange: (status: PublicationStatus | 'All') => void;
  productType: SkinSeoulProductType | 'All';
  onProductTypeChange: (type: SkinSeoulProductType | 'All') => void;
  stockStatus: StockStatus | 'All';
  onStockStatusChange: (status: StockStatus | 'All') => void;
  coupangProducts?: GpKeywordCpSearch[];
  skuSearch: string;
  onSkuSearchChange: (sku: string) => void;
  onSkuSearchClear: () => void;
  activeSkuFilter: string;
}

// Helper function to get admin URL based on environment
const getAdminUrl = (): string => {
  const hostname = window.location.hostname;

  if (hostname === 'admin.skin-seoul.work') {
    return 'https://admin.skin-seoul.work';
  } else if (hostname === 'staging.admin.skin-seoul.work') {
    return 'https://staging-admin.skin-seoul.work';
  } else if (hostname === 'dev.admin.skin-seoul.work') {
    return 'https://dev-admin.skin-seoul.work';
  } else {
    // Default to dev for localhost
    return 'https://dev-admin.skin-seoul.work';
  }
};

// Helper function to get WordPress admin URL based on environment
const getWpAdminUrl = (): string => {
  const config = getConfig();
  return config.wpAdminUrl;
};

// Function to extract Coupang product IDs from URL
const extractCoupangIds = (sourceLink: string): { cpProductId: string; cpItemId: string } | null => {
  if (!sourceLink) {
    return null;
  }

  try {
    const url = new URL(sourceLink);

    // Extract cpProductId from the path (e.g., /vp/products/{cpProductId})
    const pathMatch = url.pathname.match(/\/vp\/products\/(\d+)/);
    const cpProductId = pathMatch ? pathMatch[1] : null;

    // Extract cpItemId from query parameters
    const cpItemId = url.searchParams.get('itemId');

    if (cpProductId && cpItemId) {
      return { cpProductId, cpItemId };
    }
  } catch (error) {
    console.warn('Failed to parse Coupang URL:', sourceLink, error);
  }

  return null;
};

// Function to check if product has been updated with matching Coupang IDs
const isProductUpdated = (
  product: SkinSeoulAdminProduct,
  coupangIds: { cpProductId: string; cpItemId: string } | null,
  coupangSearchResults: GpKeywordCpSearch[],
): boolean => {
  if (!coupangIds || !product.attributes?.['cp_product_code']) {
    return false;
  }

  const expectedCpProductCode = `${coupangIds.cpProductId}-${coupangIds.cpItemId}`;
  const hasMatchingCpProductCode = product.attributes['cp_product_code'] === expectedCpProductCode;

  // Check if this product actually exists in the current Coupang search results
  const existsInSearchResults = coupangSearchResults.some(
    (coupangProduct) => coupangProduct.cpUniqueId === product.attributes?.['cp_product_code'],
  );

  return hasMatchingCpProductCode && existsInSearchResults;
};

export const SkinSeoulProductsTable: React.FC<SkinSeoulProductsTableProps> = ({
  products,
  selectedProduct,
  selectedVariation,
  onProductSelect,
  onVariationSelect,
  loading,
  totalProducts,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  postStatus,
  onPostStatusChange,
  productType,
  onProductTypeChange,
  stockStatus,
  onStockStatusChange,
  coupangProducts = [],
  skuSearch,
  onSkuSearchChange,
  onSkuSearchClear,
  activeSkuFilter,
}) => {
  const totalPages = Math.ceil(totalProducts / pageSize);

  // Client-side filters
  const [clientFilters, setClientFilters] = useState<ClientFilters>({
    hasGpId: 'all',
    wave: 'all',
    brand: '',
    category: '',
    productName: '',
    imageCount: 'all',
    cpInfo: 'all',
  });

  // Sorting state
  const [sortConfig, setSortConfig] = useState<{
    key: 'modifiedAt' | 'sku' | null;
    direction: 'asc' | 'desc';
  }>({
    key: 'modifiedAt',
    direction: 'desc', // Default to newest first
  });

  // Modal state for product details
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedSku, setSelectedSku] = useState<string>('');

  // Extract unique brands and categories for filter dropdowns
  const availableOptions = useMemo(() => {
    const brands = new Set<string>();
    const categories = new Set<string>();
    const productNames = new Set<string>();

    products.forEach((product) => {
      if (product.brand) {
        brands.add(product.brand);
      }
      product.category.forEach((cat) => categories.add(cat));
      if (product.name) {
        productNames.add(product.name);
      }
    });

    return {
      brands: Array.from(brands).sort(),
      categories: Array.from(categories).sort(),
      productNames: Array.from(productNames).sort(),
    };
  }, [products]);

  // Filter and sort products based on client filters and sort config
  const filteredProducts = useMemo(() => {
    const filtered = products.filter((product) => {
      // GP ID filter
      if (clientFilters.hasGpId !== 'all') {
        let hasGpId = Boolean(product.attributes?.['gp_product_id'] || product.attributes?.['gp_id']);

        // If no GP ID on main product, check variations
        if (!hasGpId && product.variations && product.variations.length > 0) {
          hasGpId = product.variations.some((variation) =>
            Boolean(variation.attributes?.['gp_product_id'] || variation.attributes?.['gp_id']),
          );
        }

        if ((clientFilters.hasGpId === 'yes' && !hasGpId) || (clientFilters.hasGpId === 'no' && hasGpId)) {
          return false;
        }
      }

      // Wave filter
      if (clientFilters.wave !== 'all') {
        const waveValue = product.attributes?.['wave'];
        if (clientFilters.wave === 'manual') {
          if (waveValue !== 'manual') {
            return false;
          }
        } else {
          // For numeric waves (1-6)
          if (waveValue !== clientFilters.wave) {
            return false;
          }
        }
      }

      // Brand filter
      if (clientFilters.brand && product.brand) {
        if (product.brand !== clientFilters.brand) {
          return false;
        }
      }

      // Category filter
      if (clientFilters.category) {
        const categoryMatch = product.category.includes(clientFilters.category);
        if (!categoryMatch) {
          return false;
        }
      }

      // Product name filter
      if (clientFilters.productName) {
        if (product.name !== clientFilters.productName) {
          return false;
        }
      }

      // Image count filter
      if (clientFilters.imageCount !== 'all') {
        const imageCount = product.productImageUrls?.length || 0;
        const learnMoreCount = product.attributes?.['learn_more_url']
          ? Array.isArray(product.attributes['learn_more_url'])
            ? product.attributes['learn_more_url'].length
            : 1
          : 0;

        const totalImages = imageCount + learnMoreCount;

        if (clientFilters.imageCount === 'none' && totalImages > 0) {
          return false;
        }
        if (clientFilters.imageCount === 'some' && totalImages === 0) {
          return false;
        }
      }

      // CP info filter
      if (clientFilters.cpInfo !== 'all') {
        let hasCpInfo = Boolean(product.attributes?.['cp_url'] || product.sourceLink);

        // If no CP info on main product, check variations
        if (!hasCpInfo && product.variations && product.variations.length > 0) {
          hasCpInfo = product.variations.some((variation) =>
            Boolean(variation.attributes?.['cp_url'] || variation.sourceLink),
          );
        }

        if ((clientFilters.cpInfo === 'yes' && !hasCpInfo) || (clientFilters.cpInfo === 'no' && hasCpInfo)) {
          return false;
        }
      }

      return true;
    });

    // Apply sorting
    if (sortConfig.key === 'modifiedAt') {
      filtered.sort((a, b) => {
        const aDate = new Date(a.modifiedAt || 0);
        const bDate = new Date(b.modifiedAt || 0);

        if (sortConfig.direction === 'asc') {
          return aDate.getTime() - bDate.getTime();
        } else {
          return bDate.getTime() - aDate.getTime();
        }
      });
    } else if (sortConfig.key === 'sku') {
      filtered.sort((a, b) => {
        const aSku = a.sku || '';
        const bSku = b.sku || '';

        if (sortConfig.direction === 'asc') {
          return aSku.localeCompare(bSku);
        } else {
          return bSku.localeCompare(aSku);
        }
      });
    }

    return filtered;
  }, [products, clientFilters, sortConfig]);

  const updateClientFilter = (key: keyof ClientFilters, value: ClientFilters[keyof ClientFilters]) => {
    setClientFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSort = (key: 'modifiedAt' | 'sku') => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const handleShowProductDetails = (sku: string) => {
    setSelectedSku(sku);
    setShowProductModal(true);
  };

  // State for Glowpick metadata status - removed to stop API calls

  // Render GPI (gp_product_id or gp_id) - check variations too
  const renderGpInfo = (product: SkinSeoulAdminProduct) => {
    const gpProductId = product.attributes?.['gp_product_id'];
    const gpId = product.attributes?.['gp_id'];

    // If no GP ID on main product, check variations
    let displayId = gpProductId || gpId;

    if (!displayId && product.variations && product.variations.length > 0) {
      // Check first variation that has a gp_id
      for (const variation of product.variations) {
        const varGpProductId = variation.attributes?.['gp_product_id'];
        const varGpId = variation.attributes?.['gp_id'];
        displayId = varGpProductId || varGpId;
        if (displayId) {
          break;
        }
      }
    }

    if (!displayId) {
      return <span className="text-muted">-</span>;
    }

    return (
      <div className="d-flex flex-column gap-1">
        <span
          className="text-muted user-select-all d-flex align-items-center"
          title="Click to copy GP Product ID"
          style={{
            fontSize: '0.8rem',
            cursor: 'pointer',
            padding: '1px 4px',
            borderRadius: '4px',
          }}
          onClick={(e) => {
            e.stopPropagation();
            navigator.clipboard.writeText(displayId);
          }}
        >
          {displayId}
          <Copy size={8} className="ms-1" />
        </span>
        <div className="d-flex gap-1">
          <Button
            variant="outline-primary"
            size="sm"
            style={{
              fontSize: '0.65rem',
              padding: '1px 6px',
              lineHeight: '1.2',
            }}
            onClick={(e) => {
              e.stopPropagation();
              window.open(`${getAdminUrl()}/sourcing/matching/glowpickId/${displayId}`, '_blank');
            }}
            title="Open Glowpick matching page for this GP ID"
          >
            Go to Matching
          </Button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="mt-3">Loading SkinSeoul products...</div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header>
        <div>
          <h5 className="mb-0">SkinSeoul Products ({totalProducts})</h5>
        </div>
      </Card.Header>

      {/* Server and Client Filters */}
      <Card.Body className="p-2 border-bottom">
        <Row className="g-2">
          <Col md={2}>
            <Form.Label className="mb-1" style={{ fontSize: '0.8rem', fontWeight: '500' }}>
              Product Type
            </Form.Label>
            <Form.Select
              size="sm"
              value={productType}
              onChange={(e) => onProductTypeChange(e.target.value as SkinSeoulProductType | 'All')}
            >
              <option value="All">All</option>
              <option value={SkinSeoulProductType.Simple}>Simple</option>
              <option value={SkinSeoulProductType.Variable}>Variable</option>
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Label className="mb-1" style={{ fontSize: '0.8rem', fontWeight: '500' }}>
              Post Status
            </Form.Label>
            <Form.Select
              size="sm"
              value={postStatus}
              onChange={(e) => onPostStatusChange(e.target.value as PublicationStatus | 'All')}
            >
              <option value="All">All</option>
              <option value={PublicationStatus.DRAFT}>Draft</option>
              <option value={PublicationStatus.PENDING}>Pending</option>
              <option value={PublicationStatus.PRIVATE}>Private</option>
              <option value={PublicationStatus.PUBLISH}>Publish</option>
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Label className="mb-1" style={{ fontSize: '0.8rem', fontWeight: '500' }}>
              Stock Status
            </Form.Label>
            <Form.Select
              size="sm"
              value={stockStatus}
              onChange={(e) => onStockStatusChange(e.target.value as StockStatus | 'All')}
            >
              <option value="All">All</option>
              <option value={StockStatus.IN_STOCK}>In Stock</option>
              <option value={StockStatus.OUT_OF_STOCK}>Out of Stock</option>
              <option value={StockStatus.ON_BACKORDER}>On Backorder</option>
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Label className="mb-1" style={{ fontSize: '0.8rem', fontWeight: '500' }}>
              Per Page
            </Form.Label>
            <Form.Select size="sm" value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))}>
              <option value={100}>100 per page</option>
              <option value={300}>300 per page</option>
              <option value={500}>500 per page</option>
              <option value={700}>700 per page</option>
              <option value={1000}>1000 per page</option>
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Label className="mb-1" style={{ fontSize: '0.8rem', fontWeight: '500' }}>
              Search SKU
            </Form.Label>
            <InputGroup size="sm">
              <Form.Control
                type="text"
                placeholder="Enter SKU (e.g., 100015136)"
                value={skuSearch}
                onChange={(e) => onSkuSearchChange(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              />
              {skuSearch && (
                <Button
                  variant="outline-secondary"
                  onClick={onSkuSearchClear}
                  style={{
                    borderLeft: 'none',
                    borderRadius: '0 0.375rem 0.375rem 0',
                    padding: '0.25rem 0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'transparent',
                    border: '1px solid #ced4da',
                  }}
                  title="Clear search"
                >
                  <X size={14} />
                </Button>
              )}
            </InputGroup>
          </Col>
          <Col md={2} className="d-flex align-items-end">
            <div style={{ fontSize: '0.8rem', color: '#6c757d', paddingBottom: '0.375rem', marginLeft: '1rem' }}>
              Showing {filteredProducts.length} of {totalProducts} products
              {activeSkuFilter.trim() && (
                <div style={{ fontSize: '0.7rem', fontStyle: 'italic' }}>
                  Searched by SKU: &quot;{activeSkuFilter.trim()}&quot;
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Card.Body>

      <Card.Body className="p-0" style={{ maxHeight: 'calc(100vh - 230px)', overflowY: 'auto' }}>
        <style>
          {`
            .table > tbody > tr > td {
              padding: 16px 8px !important;
              vertical-align: middle !important;
            }
            .table > thead > tr > th {
              padding: 16px 8px !important;
              vertical-align: middle !important;
            }
          `}
        </style>
        <Table hover responsive className="mb-0" style={{ tableLayout: 'fixed' }}>
          <thead className="table-light sticky-top" style={{ zIndex: 10 }}>
            <tr>
              <th style={{ width: '200px', paddingRight: '15px' }}>
                <div>
                  <div className="mb-1 text-center">Images</div>
                  <div className="d-flex justify-content-center">
                    <Form.Check
                      type="checkbox"
                      id="filter-zero-images"
                      label="0 images"
                      checked={clientFilters.imageCount === 'none'}
                      onChange={(e) => updateClientFilter('imageCount', e.target.checked ? 'none' : 'all')}
                      style={{ fontSize: '0.7rem' }}
                    />
                  </div>
                </div>
              </th>
              <th
                style={{ width: '200px', paddingRight: '15px', cursor: 'pointer' }}
                onClick={() => handleSort('sku')}
                title="Click to sort by SKU"
              >
                <div className="d-flex align-items-center">
                  SKU
                  <span
                    className="ms-2"
                    style={{
                      color: sortConfig.key === 'sku' ? '#0d6efd' : '#6c757d',
                      fontWeight: sortConfig.key === 'sku' ? 'bold' : 'normal',
                    }}
                  >
                    {sortConfig.key === 'sku' ? (sortConfig.direction === 'desc' ? '↓' : '↑') : '↕'}
                  </span>
                </div>
              </th>
              <th style={{ width: '120px', paddingRight: '15px' }}>
                <div>
                  <div className="mb-1">Brand</div>
                  <Form.Select
                    size="sm"
                    value={clientFilters.brand}
                    onChange={(e) => updateClientFilter('brand', e.target.value)}
                    style={{ fontSize: '0.7rem' }}
                  >
                    <option value="">All Brands</option>
                    {availableOptions.brands.map((brand) => (
                      <option key={brand} value={brand}>
                        {brand}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </th>
              <th style={{ width: '180px', paddingRight: '15px' }}>
                <div>
                  <div className="mb-1">Category</div>
                  <Form.Select
                    size="sm"
                    value={clientFilters.category}
                    onChange={(e) => updateClientFilter('category', e.target.value)}
                    style={{ fontSize: '0.7rem' }}
                  >
                    <option value="">All Categories</option>
                    {availableOptions.categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </th>
              <th style={{ width: '380px', paddingRight: '15px' }}>
                <div>
                  <div className="mb-1">Product Information</div>
                  <Form.Select
                    size="sm"
                    value={clientFilters.productName}
                    onChange={(e) => updateClientFilter('productName', e.target.value)}
                    style={{ fontSize: '0.7rem' }}
                  >
                    <option value="">All Products</option>
                    {availableOptions.productNames.map((productName) => (
                      <option key={productName} value={productName}>
                        {productName.length > 40 ? `${productName.substring(0, 40)}...` : productName}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </th>
              <th style={{ width: '120px', paddingRight: '15px' }}>Status</th>
              <th
                style={{ width: '150px', paddingRight: '', cursor: 'pointer' }}
                onClick={() => handleSort('modifiedAt')}
                title="Click to sort by modified date"
              >
                <div className="d-flex align-items-center">
                  Modified
                  <span
                    className="ms-1"
                    style={{
                      color: sortConfig.key === 'modifiedAt' ? '#0d6efd' : '#6c757d',
                      fontWeight: sortConfig.key === 'modifiedAt' ? 'bold' : 'normal',
                    }}
                  >
                    {sortConfig.key === 'modifiedAt' ? (sortConfig.direction === 'desc' ? '↓' : '↑') : '↕'}
                  </span>
                </div>
              </th>
              <th style={{ width: '80px', paddingRight: '15px' }}>
                <div>
                  <div className="mb-1">Wave</div>
                  <Form.Select
                    size="sm"
                    value={clientFilters.wave}
                    onChange={(e) => updateClientFilter('wave', e.target.value as ClientFilters['wave'])}
                    style={{ fontSize: '0.7rem' }}
                  >
                    <option value="all">All</option>
                    <option value="manual">Manual</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                    <option value="6">6</option>
                  </Form.Select>
                </div>
              </th>
              <th style={{ width: '160px', paddingRight: '15px' }}>
                <div>
                  <div className="mb-1">CP</div>
                  <Form.Select
                    size="sm"
                    value={clientFilters.cpInfo || 'all'}
                    onChange={(e) => updateClientFilter('cpInfo', e.target.value as 'all' | 'yes' | 'no')}
                    style={{ fontSize: '0.7rem' }}
                  >
                    <option value="all">All</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </Form.Select>
                </div>
              </th>
              <th style={{ width: '120px' }}>
                <div>
                  <div className="mb-1">GPI</div>
                  <Form.Select
                    size="sm"
                    value={clientFilters.hasGpId}
                    onChange={(e) => updateClientFilter('hasGpId', e.target.value as 'all' | 'yes' | 'no')}
                    style={{ fontSize: '0.7rem' }}
                  >
                    <option value="all">All</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </Form.Select>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts
              .map((product, productIndex) => {
                const rows = [];

                // Add parent product row
                rows.push(
                  <tr
                    key={product.productId}
                    className={selectedProduct?.productId === product.productId ? 'table-active' : ''}
                    style={{ cursor: 'pointer' }}
                    onClick={() => onProductSelect(product)}
                  >
                    <td>
                      <div className="d-flex align-items-center" style={{ marginLeft: '10px', marginRight: '20px' }}>
                        <div
                          className="text-muted d-flex align-items-center justify-content-center me-2"
                          style={{
                            width: '20px',
                            height: '20px',
                            fontSize: '0.7rem',
                            fontWeight: '500',
                          }}
                        >
                          {productIndex + 1}
                        </div>
                        <div>
                          <a
                            href={product.productLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-decoration-none"
                            style={{ cursor: 'pointer' }}
                          >
                            {product.productImageUrls?.[0] ? (
                              <Image
                                src={product.productImageUrls[0]}
                                alt={product.name}
                                width={140}
                                height={140}
                                className="rounded"
                                style={{ objectFit: 'cover' }}
                              />
                            ) : (
                              <div
                                className="rounded d-flex align-items-center justify-content-center"
                                style={{
                                  width: '140px',
                                  height: '140px',
                                  backgroundColor: '#f8f9fa',
                                  border: '1px solid #dee2e6',
                                }}
                              >
                                <Package size={24} className="text-muted" />
                              </div>
                            )}
                          </a>

                          {/* Image count directly under thumbnail */}
                          <div className="text-center mt-1" style={{ fontSize: '0.7rem', color: '#666' }}>
                            {product.productImageUrls?.length || 0} thu,{' '}
                            {product.attributes?.['learn_more_url']
                              ? Array.isArray(product.attributes['learn_more_url'])
                                ? product.attributes['learn_more_url'].length
                                : 1
                              : 0}{' '}
                            pdp
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="ids-info">
                        <div className="mb-3">
                          <Badge
                            bg={getProductTypeColor(product.type || SkinSeoulProductType.Simple)}
                            style={{ fontSize: '0.65rem' }}
                          >
                            {product.type === SkinSeoulProductType.Variable ? 'Variable' : 'Simple'}
                          </Badge>
                        </div>
                        <div className="mb-1">
                          <div className="d-flex align-items-center gap-1 mb-1">
                            <small className="text-muted">Post:</small>
                            <span
                              className="text-muted user-select-all d-flex align-items-center"
                              title="Click to copy Product ID"
                              style={{
                                fontSize: '0.8rem',
                                cursor: 'pointer',
                                padding: '1px 4px',
                                borderRadius: '4px',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(product.productId!);
                              }}
                            >
                              {product.productId}
                              <Copy size={9} className="ms-1" />
                            </span>
                          </div>
                          <div className="d-flex align-items-center">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="d-flex align-items-center"
                              style={{
                                fontSize: '0.7rem',
                                padding: '1px 6px',
                                lineHeight: '1.2',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(
                                  `${getWpAdminUrl()}post.php?post=${product.productId}&action=edit`,
                                  '_blank',
                                );
                              }}
                              title="Open WordPress admin edit page"
                            >
                              Go to WP Edit
                              <ExternalLink size={9} className="ms-1" />
                            </Button>
                          </div>
                        </div>
                        <div>
                          <div className="d-flex align-items-center gap-1 mb-1">
                            <small className="text-muted">SKU:</small>
                            <span
                              className="text-muted user-select-all d-flex align-items-center"
                              title="Click to copy SKU"
                              style={{
                                fontSize: '0.8rem',
                                cursor: 'pointer',
                                padding: '1px 4px',
                                borderRadius: '4px',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(product.sku);
                              }}
                            >
                              {product.sku}
                              <Copy size={9} className="ms-1" />
                            </span>
                          </div>
                          <div className="d-flex align-items-center">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="d-flex align-items-center"
                              style={{
                                fontSize: '0.7rem',
                                padding: '1px 6px',
                                lineHeight: '1.2',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShowProductDetails(product.sku);
                              }}
                              title="View product details"
                            >
                              View SKU
                              <ExternalLink size={9} className="ms-1" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: '0.85rem', fontWeight: '500' }}>{product.brand || '-'}</div>
                    </td>
                    <td>
                      <div
                        style={{
                          fontSize: '0.8rem',
                          color: product.category.includes('Uncategorized') ? '#dc3545' : 'inherit',
                        }}
                      >
                        {product.category.join(' › ')}
                      </div>
                    </td>
                    <td>
                      <div className="ss-product-info">
                        {/* Product name row */}
                        <h1
                          className="fw-bold mb-1"
                          style={{
                            fontSize: '0.95rem',
                            lineHeight: '1.2',
                            wordBreak: 'break-word',
                          }}
                        >
                          {product.name}
                        </h1>
                        {product.nameKor && (
                          <div className="mb-1 text-muted" style={{ fontSize: '0.85rem' }}>
                            {product.nameKor}
                          </div>
                        )}

                        {/* Size, Pack, and Color information */}
                        <div className="d-flex gap-2 mt-2 flex-wrap">
                          {product['size'] && (
                            <div className="d-flex align-items-center">
                              <small className="text-muted me-1">Size:</small>
                              <Badge bg="light" text="dark" style={{ fontSize: '0.7rem' }}>
                                {product['size']}
                              </Badge>
                            </div>
                          )}
                          {product['pack'] && (
                            <div className="d-flex align-items-center">
                              <small className="text-muted me-1">Pack:</small>
                              <Badge bg="light" text="dark" style={{ fontSize: '0.7rem' }}>
                                {product['pack']}
                              </Badge>
                            </div>
                          )}
                          {product.attributes?.['attribute_color'] && (
                            <div className="d-flex align-items-center">
                              <small className="text-muted me-1">Color:</small>
                              <Badge bg="info" text="white" style={{ fontSize: '0.7rem' }}>
                                {product.attributes['attribute_color']}
                              </Badge>
                            </div>
                          )}
                        </div>

                        {/* Price information */}
                        {(product.originalPrice ||
                          (product.additionalPrices && product.additionalPrices.length > 0)) && (
                          <div className="mt-2">
                            <div style={{ fontSize: '0.85rem', fontWeight: '500' }}>
                              {product.additionalPrices &&
                              product.additionalPrices.length > 0 &&
                              product.additionalPrices[0]?.salesPrice ? (
                                // Sale price exists - show sale format
                                <>
                                  <span className="text-danger me-2">
                                    {product.additionalPrices[0].currency}
                                    {product.additionalPrices[0].salesPrice.toFixed(2)}
                                  </span>
                                  {product.originalPrice && (
                                    <span className="text-muted text-decoration-line-through me-2">
                                      {product.currency || 'SGD'}
                                      {product.originalPrice.toFixed(2)}
                                    </span>
                                  )}
                                  {product.originalPrice && (
                                    <span className="badge bg-danger text-white" style={{ fontSize: '0.65rem' }}>
                                      {Math.round(
                                        ((product.originalPrice - product.additionalPrices[0].salesPrice) /
                                          product.originalPrice) *
                                          100,
                                      )}
                                      % OFF
                                    </span>
                                  )}
                                </>
                              ) : (
                                // No sale price - show original price as regular price
                                product.originalPrice && (
                                  <span className="text-dark">
                                    {product.currency || 'SGD'}
                                    {product.originalPrice.toFixed(2)}
                                  </span>
                                )
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex flex-column gap-1">
                        <Badge bg={getPublicationStatusColor(product.postStatus!)} style={{ fontSize: '0.65rem' }}>
                          {product.postStatus || PublicationStatus.DRAFT}
                        </Badge>
                        <Badge bg={getStockStatusColor(product.status!)} style={{ fontSize: '0.65rem' }}>
                          {product.status === StockStatus.OUT_OF_STOCK
                            ? 'Out of Stock'
                            : product.status === StockStatus.IN_STOCK
                              ? 'In Stock'
                              : product.status === StockStatus.ON_BACKORDER
                                ? 'On Backorder'
                                : product.status || 'Out of Stock'}
                        </Badge>
                        <div style={{ fontSize: '0.65rem', color: '#6c757d', textAlign: 'center' }}>
                          {product.attributes?.['total_sales'] || '0'} sold
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: '0.75rem', textAlign: 'center' }}>
                        {product.modifiedAt ? product.modifiedAt.toString().replace('.000Z', 'Z') : '-'}
                      </div>
                    </td>
                    <td>
                      {product.attributes?.['wave'] ? (
                        <Badge bg="info" style={{ fontSize: '0.65rem' }}>
                          {product.attributes['wave']}
                        </Badge>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {(() => {
                        // Check for cp_url in attributes first, then fallback to sourceLink
                        const cpUrl = product.attributes?.['cp_url'] || product.sourceLink;

                        if (cpUrl) {
                          const coupangIds = extractCoupangIds(cpUrl);
                          const isUpdated = isProductUpdated(product, coupangIds, coupangProducts);

                          return coupangIds ? (
                            <div className="d-flex flex-column gap-1">
                              {/* Updated Badge */}
                              {isUpdated && (
                                <div className="mb-1">
                                  <Badge bg="success" style={{ fontSize: '0.65rem' }}>
                                    ✓ Updated
                                  </Badge>
                                </div>
                              )}
                              {/* Coupang Product IDs - Clickable for copy */}
                              <div className="d-flex align-items-center gap-1 mb-1">
                                <span
                                  className="text-muted user-select-all"
                                  style={{
                                    fontSize: '0.75rem',
                                    cursor: 'pointer',
                                    fontFamily: 'monospace',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const idText = `${coupangIds.cpProductId}-${coupangIds.cpItemId}`;
                                    navigator.clipboard.writeText(idText);
                                    // You could add a toast notification here if available
                                  }}
                                  title={`Click to copy: ${coupangIds.cpProductId}-${coupangIds.cpItemId}`}
                                >
                                  {coupangIds.cpProductId}-{coupangIds.cpItemId}
                                </span>
                                <Copy size={12} className="text-muted" />
                              </div>
                              {/* External links */}
                              <div className="d-flex flex-column gap-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(cpUrl, '_blank');
                                  }}
                                  className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-1"
                                  style={{ fontSize: '0.7rem' }}
                                >
                                  view on coupang
                                  <ExternalLink size={10} />
                                </button>
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  className="d-flex align-items-center"
                                  style={{
                                    fontSize: '0.7rem',
                                    padding: '1px 6px',
                                    lineHeight: '1.2',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(
                                      `${getAdminUrl()}/sourcing/matched/${coupangIds.cpProductId}/${coupangIds.cpItemId}`,
                                      '_blank',
                                    );
                                  }}
                                  title="Open matched page for this Coupang product"
                                >
                                  Go to Matched
                                  <ExternalLink size={9} className="ms-1" />
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <a
                              href={cpUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-decoration-none d-flex align-items-center gap-1"
                              style={{ fontSize: '0.85rem' }}
                            >
                              coupang
                              <ExternalLink size={12} />
                            </a>
                          );
                        }

                        return <span className="text-muted">-</span>;
                      })()}
                    </td>
                    <td>
                      <div className="gp-info">{renderGpInfo(product)}</div>
                    </td>
                  </tr>,
                );

                // Add variation rows if this is a variable product
                if (
                  product.type === SkinSeoulProductType.Variable &&
                  product.variations &&
                  product.variations.length > 0
                ) {
                  product.variations.forEach((variation, index) => {
                    rows.push(
                      <tr
                        key={`${product.productId}-var-${variation.productId || index}`}
                        className={selectedVariation?.productId === variation.productId ? 'table-active' : ''}
                        style={{ cursor: 'pointer' }}
                        onClick={() => onVariationSelect(variation)}
                      >
                        <td>
                          <div className="d-flex align-items-center" style={{ marginLeft: '10px' }}>
                            <div style={{ paddingLeft: '8px', width: '20px', textAlign: 'center' }}>
                              <ChevronRight size={18} className="text-primary" />
                            </div>
                            <div className="position-relative">
                              <div>
                                <a
                                  href={variation.productLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-decoration-none"
                                  style={{ cursor: 'pointer' }}
                                >
                                  {variation.productImageUrls?.[0] || product.productImageUrls?.[0] ? (
                                    <Image
                                      src={variation.productImageUrls?.[0] || product.productImageUrls?.[0]}
                                      alt={variation.name}
                                      width={140}
                                      height={140}
                                      className="rounded"
                                      style={{ objectFit: 'cover' }}
                                    />
                                  ) : (
                                    <div
                                      className="rounded d-flex align-items-center justify-content-center"
                                      style={{
                                        width: '140px',
                                        height: '140px',
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                      }}
                                    >
                                      <Package size={24} className="text-muted" />
                                    </div>
                                  )}
                                </a>

                                {/* Image count directly under variation thumbnail */}
                                <div className="text-center mt-1" style={{ fontSize: '0.65rem', color: '#666' }}>
                                  {variation.productImageUrls?.length || 0} thu,{' '}
                                  {variation.attributes?.['learn_more_url']
                                    ? Array.isArray(variation.attributes['learn_more_url'])
                                      ? variation.attributes['learn_more_url'].length
                                      : 1
                                    : 0}{' '}
                                  pdp
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="ids-info" style={{ paddingLeft: '20px' }}>
                            <div className="mb-2">
                              <Badge bg="warning" style={{ fontSize: '0.65rem' }}>
                                Variation #{index + 1}
                              </Badge>
                            </div>
                            <div className="mb-1">
                              <div className="d-flex align-items-center gap-1 mb-1">
                                <small className="text-muted">Post:</small>
                                <span
                                  className="text-muted user-select-all d-flex align-items-center"
                                  title="Click to copy Product ID"
                                  style={{
                                    fontSize: '0.75rem',
                                    cursor: 'pointer',
                                    padding: '1px 4px',
                                    borderRadius: '4px',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(variation.productId!);
                                  }}
                                >
                                  {variation.productId}
                                  <Copy size={8} className="ms-1" />
                                </span>
                              </div>
                              <div className="d-flex align-items-center">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="d-flex align-items-center"
                                  style={{
                                    fontSize: '0.65rem',
                                    padding: '1px 6px',
                                    lineHeight: '1.2',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(
                                      `${getWpAdminUrl()}post.php?post=${variation.productId}&action=edit`,
                                      '_blank',
                                    );
                                  }}
                                  title="Open WordPress admin edit page"
                                >
                                  Go to WP Edit
                                  <ExternalLink size={8} className="ms-1" />
                                </Button>
                              </div>
                            </div>
                            <div>
                              <div className="d-flex align-items-center gap-1 mb-1">
                                <small className="text-muted">SKU:</small>
                                <span
                                  className="text-muted user-select-all d-flex align-items-center"
                                  title="Click to copy SKU"
                                  style={{
                                    fontSize: '0.75rem',
                                    cursor: 'pointer',
                                    padding: '1px 4px',
                                    borderRadius: '4px',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(variation.sku);
                                  }}
                                >
                                  {variation.sku}
                                  <Copy size={8} className="ms-1" />
                                </span>
                              </div>
                              <div className="d-flex align-items-center">
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  className="d-flex align-items-center"
                                  style={{
                                    fontSize: '0.65rem',
                                    padding: '1px 6px',
                                    lineHeight: '1.2',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleShowProductDetails(variation.sku);
                                  }}
                                  title="View product details"
                                >
                                  View SKU
                                  <ExternalLink size={8} className="ms-1" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div style={{ fontSize: '0.8rem', fontWeight: '500', paddingLeft: '20px' }}>
                            {variation.brand || '-'}
                          </div>
                        </td>
                        <td>
                          <div
                            style={{
                              fontSize: '0.75rem',
                              paddingLeft: '20px',
                              color: variation.category.includes('Uncategorized') ? '#dc3545' : 'inherit',
                            }}
                          >
                            {variation.category.join(' › ')}
                          </div>
                        </td>
                        <td>
                          <div className="ss-product-info" style={{ paddingLeft: '20px' }}>
                            {/* Product name row */}
                            <h1
                              className="fw-bold mb-1"
                              style={{
                                fontSize: '0.9rem',
                                lineHeight: '1.2',
                                wordBreak: 'break-word',
                              }}
                            >
                              {variation.name}
                            </h1>
                            {variation.nameKor && (
                              <div className="mb-1 text-muted" style={{ fontSize: '0.8rem' }}>
                                {variation.nameKor}
                              </div>
                            )}

                            {/* Size, Pack, and Color information for variations */}
                            <div className="d-flex gap-2 mt-2 flex-wrap">
                              {variation['size'] && (
                                <div className="d-flex align-items-center">
                                  <small className="text-muted me-1">Size:</small>
                                  <Badge bg="light" text="dark" style={{ fontSize: '0.7rem' }}>
                                    {variation['size']}
                                  </Badge>
                                </div>
                              )}
                              {variation['pack'] && (
                                <div className="d-flex align-items-center">
                                  <small className="text-muted me-1">Pack:</small>
                                  <Badge bg="light" text="dark" style={{ fontSize: '0.7rem' }}>
                                    {variation['pack']}
                                  </Badge>
                                </div>
                              )}
                              {variation.attributes?.['attribute_color'] && (
                                <div className="d-flex align-items-center">
                                  <small className="text-muted me-1">Color:</small>
                                  <Badge bg="info" text="white" style={{ fontSize: '0.7rem' }}>
                                    {variation.attributes['attribute_color']}
                                  </Badge>
                                </div>
                              )}
                            </div>

                            {/* Price information for variations */}
                            {(variation.originalPrice ||
                              (variation.additionalPrices && variation.additionalPrices.length > 0)) && (
                              <div className="mt-2">
                                <div style={{ fontSize: '0.8rem', fontWeight: '500' }}>
                                  {variation.additionalPrices &&
                                  variation.additionalPrices.length > 0 &&
                                  variation.additionalPrices[0]?.salesPrice ? (
                                    // Sale price exists - show sale format
                                    <>
                                      <span className="text-danger me-2">
                                        {variation.additionalPrices[0].currency}
                                        {variation.additionalPrices[0].salesPrice.toFixed(2)}
                                      </span>
                                      {variation.originalPrice && (
                                        <span className="text-muted text-decoration-line-through me-2">
                                          {variation.currency || 'SGD'}
                                          {variation.originalPrice.toFixed(2)}
                                        </span>
                                      )}
                                      {variation.originalPrice && (
                                        <span className="badge bg-danger text-white" style={{ fontSize: '0.6rem' }}>
                                          {Math.round(
                                            ((variation.originalPrice - variation.additionalPrices[0].salesPrice) /
                                              variation.originalPrice) *
                                              100,
                                          )}
                                          % OFF
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    // No sale price - show original price as regular price
                                    variation.originalPrice && (
                                      <span className="text-dark">
                                        {variation.currency || 'SGD'}
                                        {variation.originalPrice.toFixed(2)}
                                      </span>
                                    )
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column gap-1">
                            <Badge bg={getPublicationStatusColor(variation.postStatus!)} style={{ fontSize: '0.6rem' }}>
                              {variation.postStatus}
                            </Badge>
                            <Badge bg={getStockStatusColor(variation.status!)} style={{ fontSize: '0.6rem' }}>
                              {variation.status === StockStatus.OUT_OF_STOCK
                                ? 'Out of Stock'
                                : variation.status === StockStatus.IN_STOCK
                                  ? 'In Stock'
                                  : variation.status === StockStatus.ON_BACKORDER
                                    ? 'On Backorder'
                                    : variation.status || 'Out of Stock'}
                            </Badge>
                            <div style={{ fontSize: '0.6rem', color: '#6c757d', textAlign: 'center' }}>
                              {variation.attributes?.['total_sales'] || '0'} sold
                            </div>
                          </div>
                        </td>
                        <td>
                          <div style={{ fontSize: '0.7rem', textAlign: 'center' }}>
                            {variation.modifiedAt ? variation.modifiedAt.toString().replace('.000Z', 'Z') : '-'}
                          </div>
                        </td>
                        <td>
                          {variation.attributes?.['wave'] ? (
                            <Badge bg="info" style={{ fontSize: '0.6rem' }}>
                              {variation.attributes['wave']}
                            </Badge>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          {(() => {
                            // Check for cp_url in attributes first, then fallback to sourceLink
                            const cpUrl = variation.attributes?.['cp_url'] || variation.sourceLink;

                            if (cpUrl) {
                              const coupangIds = extractCoupangIds(cpUrl);
                              const isUpdated = isProductUpdated(variation, coupangIds, coupangProducts);

                              return coupangIds ? (
                                <div className="d-flex flex-column gap-1">
                                  {/* Updated Badge */}
                                  {isUpdated && (
                                    <div className="mb-1">
                                      <Badge bg="success" style={{ fontSize: '0.6rem' }}>
                                        ✓ Updated
                                      </Badge>
                                    </div>
                                  )}
                                  {/* Coupang Product IDs - Clickable for copy */}
                                  <div className="d-flex align-items-center gap-1 mb-1">
                                    <span
                                      className="text-muted user-select-all"
                                      style={{
                                        fontSize: '0.7rem',
                                        cursor: 'pointer',
                                        fontFamily: 'monospace',
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const idText = `${coupangIds.cpProductId}-${coupangIds.cpItemId}`;
                                        navigator.clipboard.writeText(idText);
                                      }}
                                      title={`Click to copy: ${coupangIds.cpProductId}-${coupangIds.cpItemId}`}
                                    >
                                      {coupangIds.cpProductId}-{coupangIds.cpItemId}
                                    </span>
                                    <Copy size={10} className="text-muted" />
                                  </div>
                                  {/* External links */}
                                  <div className="d-flex flex-column gap-1">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(cpUrl, '_blank');
                                      }}
                                      className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-1"
                                      style={{ fontSize: '0.65rem' }}
                                    >
                                      view on coupang
                                      <ExternalLink size={9} />
                                    </button>
                                    <Button
                                      variant="outline-success"
                                      size="sm"
                                      className="d-flex align-items-center"
                                      style={{
                                        fontSize: '0.65rem',
                                        padding: '1px 6px',
                                        lineHeight: '1.2',
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(
                                          `${getAdminUrl()}/sourcing/matched/${coupangIds.cpProductId}/${coupangIds.cpItemId}`,
                                          '_blank',
                                        );
                                      }}
                                      title="Open matched page for this Coupang product"
                                    >
                                      Go to Matched
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <a
                                  href={cpUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-decoration-none d-flex align-items-center gap-1"
                                  style={{ fontSize: '0.8rem' }}
                                >
                                  coupang
                                  <ExternalLink size={10} />
                                </a>
                              );
                            }

                            return <span className="text-muted">-</span>;
                          })()}
                        </td>
                        <td>
                          <div className="gp-info">{renderGpInfo(variation)}</div>
                        </td>
                      </tr>,
                    );
                  });
                }

                return rows;
              })
              .flat()}
          </tbody>
        </Table>

        {filteredProducts.length === 0 && (
          <div className="text-center py-5">
            <Package size={48} className="text-muted mb-3" />
            <h5 className="text-muted">No products found</h5>
            <p className="text-muted">Try adjusting your filters</p>
          </div>
        )}
      </Card.Body>

      {totalPages > 1 && (
        <Card.Footer>
          <div className="d-flex justify-content-between align-items-center">
            <small className="text-muted">
              Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalProducts)} of{' '}
              {totalProducts} products
            </small>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                </li>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  return (
                    <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => onPageChange(pageNum)}>
                        {pageNum}
                      </button>
                    </li>
                  );
                })}

                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </Card.Footer>
      )}

      {/* Product Details Modal */}
      <ProductDetailsModal
        show={showProductModal}
        onHide={() => setShowProductModal(false)}
        sku={selectedSku}
        postStatus={postStatus === 'All' ? undefined : postStatus}
      />
    </Card>
  );
};
